import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Card, Tag, Typography } from 'antd';
import { TraceMode } from '@evaltick/shared';
import type { ColumnsType } from 'antd/es/table';

import { useAuth } from '@clerk/clerk-react';

import { ROUTES, buildRoute } from '@/routes';
import { AdminService, type AdminTraceListItem, type TraceStatus } from '@/services/adminService';
import { DataTable } from '@/components/DataTable';
import { formatDateTime, formatDuration } from '@/utils/formatters';
import { ADMIN_TABLE_PAGE_SIZE } from '@/utils/constants/admin';
import { TRANSITION_DURATION_SLOW } from '@/styles';
import styles from './AdminTraces.module.scss';

// Antd's Table has no exit transition for tree-child rows (unlike its Collapse,
// which AdminTraceDetail uses for span nesting) — collapsed rows are just removed
// from the DOM. To get a matching animated close, a collapsing session's rows stay
// mounted (and keep playing) for one animation length before we actually drop them.
const COLLAPSE_ANIMATION_MS = parseInt(TRANSITION_DURATION_SLOW, 10);

// The session badge shows only the leading segment of the threadId (full id on hover).
const SESSION_ID_DISPLAY_LENGTH = 8;

const STATUS_TAG_COLORS: Record<TraceStatus, string> = {
  success: 'success',
  error: 'error',
  running: 'processing',
};

// Worse status wins when rolling several traces up into one session summary.
const STATUS_SEVERITY: TraceStatus[] = ['error', 'running', 'success'];

interface TraceRow extends AdminTraceListItem {
  isGroupRow?: boolean;
  isChildRow?: boolean;
  traceCount?: number;
  children?: TraceRow[];
  parentKey?: string;
}

// Evaluate/Clarify calls from the same conversation share a threadId. Traces
// that share one are rolled up into a single expandable "session" row
// (antd's Table auto-renders nested rows from a `children` array) instead of
// relying on a visual convention the admin has to notice.
const buildRows = (traces: AdminTraceListItem[]): TraceRow[] => {
  const order: string[] = [];
  const byKey = new Map<string, AdminTraceListItem[]>();

  traces.forEach((trace, index) => {
    const key = trace.threadId ?? `__ungrouped_${index}`;
    if (!byKey.has(key)) {
      order.push(key);
      byKey.set(key, []);
    }
    byKey.get(key)!.push(trace);
  });

  return order.map((key) => {
    const group = byKey.get(key)!;
    if (group.length === 1) return group[0];

    const chronological = [...group].sort(
      (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
    );
    const stillRunning = chronological.some((t) => !t.endedAt);
    const endedAt = stillRunning
      ? null
      : chronological.reduce(
          (latest, t) => (new Date(t.endedAt!) > new Date(latest) ? t.endedAt! : latest),
          chronological[0].endedAt!,
        );
    const status = STATUS_SEVERITY.find((s) => group.some((t) => t.status === s)) ?? 'success';
    const groupTraceId = `session-${key}`;

    return {
      traceId: groupTraceId,
      threadId: key,
      name: 'session',
      spanType: 'session',
      status,
      entityName: null,
      startedAt: chronological[0].startedAt,
      endedAt,
      error: null,
      competencyId: group[0].competencyId,
      questionType: group[0].questionType,
      isGroupRow: true,
      traceCount: group.length,
      children: chronological.map((trace) => ({
        ...trace,
        isChildRow: true,
        parentKey: groupTraceId,
      })),
    };
  });
};

const buildColumns = (onView: (traceId: string) => void): ColumnsType<TraceRow> => [
  {
    title: 'Session',
    dataIndex: 'threadId',
    key: 'threadId',
    width: 140,
    align: 'center',
    render: (threadId: string | undefined, record) => {
      if (record.isChildRow) return null;
      // Coach traces group by threadId; sessionSummaryAgent has no Memory
      // thread, so it carries the assessmentId instead — same badge either way.
      const sessionId = threadId ?? record.assessmentId;
      return sessionId ? (
        <span className={styles.sessionBadge} title={sessionId}>
          {sessionId.slice(0, SESSION_ID_DISPLAY_LENGTH)}
        </span>
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      );
    },
  },
  {
    title: 'Competency',
    dataIndex: 'competencyId',
    key: 'competencyId',
    width: 240,
    ellipsis: true,
    render: (competencyId: string | undefined, record) => (
      <span className={styles.competencyCell}>
        {competencyId ?? <Typography.Text type="secondary">—</Typography.Text>}
        {record.isGroupRow && <Tag className={styles.countTag}>{record.traceCount} traces</Tag>}
      </span>
    ),
  },
  {
    title: 'Question Type',
    dataIndex: 'questionType',
    key: 'questionType',
    width: 140,
    align: 'center',
    render: (questionType?: string) =>
      questionType ? <Tag>{questionType}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
  },
  {
    title: 'Phase',
    dataIndex: 'phase',
    key: 'phase',
    width: 90,
    align: 'center',
    render: (phase: string | undefined, record) =>
      record.isGroupRow ? null : phase ?? <Typography.Text type="secondary">—</Typography.Text>,
  },
  {
    title: 'Mode',
    dataIndex: 'mode',
    key: 'mode',
    width: 80,
    align: 'center',
    // A session can mix a voice trace with text-mode ones — shown per trace,
    // not on the group summary row (same convention as Phase above).
    render: (mode: string | undefined, record) =>
      record.isGroupRow ? null : mode === TraceMode.Voice ? <Tag color="purple">Voice</Tag> : 'Text',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 90,
    align: 'center',
    render: (status: TraceStatus) => <Tag color={STATUS_TAG_COLORS[status]}>{status}</Tag>,
  },
  {
    title: 'Started',
    key: 'startedAt',
    width: 170,
    render: (_, record) => formatDateTime(record.startedAt),
  },
  {
    title: 'Duration',
    key: 'duration',
    width: 80,
    align: 'right',
    render: (_, record) => formatDuration(record.startedAt, record.endedAt),
  },
  {
    title: '',
    key: 'actions',
    width: 60,
    fixed: 'right',
    align: 'right',
    render: (_, record) =>
      record.isGroupRow ? null : <a onClick={() => onView(record.traceId)}>View</a>,
  },
];

export const AdminTraces: React.FC = () => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const [traces, setTraces] = useState<AdminTraceListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // `openKeys` mirrors antd's expandedRowKeys; a key being collapsed stays in it
  // (so its rows remain mounted and play the closing animation) until `closingKeys`
  // drops it and the timeout below actually removes it.
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [closingKeys, setClosingKeys] = useState<Set<string>>(new Set());
  const closeTimeouts = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const timeouts = closeTimeouts.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const handleExpandedRowsChange = useCallback((keys: readonly React.Key[]) => {
    const nextKeys = keys.map(String);
    const nextSet = new Set(nextKeys);

    setOpenKeys((prev) => Array.from(new Set([...prev, ...nextKeys])));

    // Reopening a key mid-close cancels its pending removal.
    setClosingKeys((prev) => {
      const reopened = nextKeys.filter((key) => prev.has(key));
      if (!reopened.length) return prev;
      reopened.forEach((key) => {
        const timeout = closeTimeouts.current.get(key);
        if (timeout) {
          clearTimeout(timeout);
          closeTimeouts.current.delete(key);
        }
      });
      const next = new Set(prev);
      reopened.forEach((key) => next.delete(key));
      return next;
    });

    const closed = openKeys.filter((key) => !nextSet.has(key));
    if (!closed.length) return;

    setClosingKeys((prev) => new Set([...prev, ...closed]));
    closed.forEach((key) => {
      const timeout = setTimeout(() => {
        closeTimeouts.current.delete(key);
        setClosingKeys((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        setOpenKeys((prev) => prev.filter((k) => k !== key));
      }, COLLAPSE_ANIMATION_MS);
      closeTimeouts.current.set(key, timeout);
    });
  }, [openKeys]);

  const fetchTraces = useCallback(
    async (p: number) => {
      setLoading(true);
      try {
        const token = await getToken();
        if (!token) return;
        const result = await new AdminService(token).listTraces(p, ADMIN_TABLE_PAGE_SIZE);
        setTraces(result.data);
        setTotalCount(result.pagination.totalCount);
      } catch (e) {
        console.error(e);
        notification.error({ message: 'Failed to load traces' });
      } finally {
        setLoading(false);
      }
    },
    [getToken, notification],
  );

  useEffect(() => {
    fetchTraces(page);
    setOpenKeys([]);
    setClosingKeys(new Set());
  }, [page, fetchTraces]);

  const handleView = (traceId: string) => {
    navigate(buildRoute(ROUTES.ADMIN_TRACE_DETAIL, { traceId }));
  };

  const columns = buildColumns(handleView);
  const rows = useMemo(() => buildRows(traces), [traces]);

  const rowClassName = (record: TraceRow) => {
    if (record.isGroupRow) return styles.sessionGroupRow;
    if (!record.isChildRow) return '';
    return record.parentKey && closingKeys.has(record.parentKey)
      ? `${styles.sessionChildRow} ${styles.sessionChildRowClosing}`
      : styles.sessionChildRow;
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <Typography.Title level={2} className={styles.title}>
            Traces
          </Typography.Title>
          <Typography.Text type="secondary" className={styles.pageSub}>
            Inspect Mastra agent traces for debugging.
          </Typography.Text>
        </div>
      </div>

      <Card className={styles.tableCard}>
        <DataTable
          dataSource={rows}
          columns={columns}
          rowKey="traceId"
          loading={loading}
          page={page}
          totalCount={totalCount}
          pageSize={ADMIN_TABLE_PAGE_SIZE}
          onPageChange={setPage}
          rowClassName={rowClassName}
          expandable={{
            expandedRowKeys: openKeys,
            onExpandedRowsChange: handleExpandedRowsChange,
            expandRowByClick: true,
          }}
        />
      </Card>
    </div>
  );
};

export default AdminTraces;
